import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import qrcode from 'qrcode-generator';
import {
  CLOUD_PROVIDER_NAMES,
  CLOUD_PROVIDERS,
  orderMessengersForRegion,
  TERMS_VERSION,
  type CloudConnection,
  type CloudProvider,
  type ConnectLoginView,
} from '../../shared/src/types/connect';

/** Messengers' names as a sentence lists them: "WhatsApp, Signal or Telegram". */
export function messengerNames(providers: readonly CloudProvider[], joiner: 'or' | 'and' = 'or') {
  const names = providers.map((provider) => CLOUD_PROVIDER_NAMES[provider]);
  return names.length < 2
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} ${joiner} ${names[names.length - 1]}`;
}

/** How to link each messenger, where its code arrives, and anything to know before linking it. */
const HOW_TO_LINK: Record<CloudProvider, { steps: string[]; code: string; note?: string }> = {
  whatsapp: {
    steps: [
      'Open WhatsApp on your phone.',
      'Go to Settings (⋮ on Android) → Linked devices → Link a device.',
      'Point your phone at this code.',
    ],
    code: 'The code in your WhatsApp chat with yourself',
    note: 'SoulThread links to WhatsApp the way WhatsApp on a computer does, but WhatsApp doesn’t make it, and WhatsApp may limit accounts that use apps it didn’t make.',
  },
  signal: {
    steps: [
      'Open Signal on your phone.',
      'Go to Settings → Linked devices → Link new device.',
      'Point your phone at this code.',
    ],
    code: 'The code in your Signal Note to Self',
    note: 'SoulThread links to Signal the way Signal on a computer does, with software Signal doesn’t make. Signal gives a newly linked device only the messages that arrive from then on, so your conversations fill in as you talk.',
  },
  telegram: {
    steps: [
      'Open Telegram on your phone.',
      'Go to Settings → Devices → Link Desktop Device.',
      'Point your phone at this code.',
    ],
    code: 'The code in your Telegram Saved Messages',
  },
};

interface Login {
  loginId: string;
  secret: string;
}

const POLL_MS = 1500;

async function connectApi<T>(path: string, body: object, token?: string): Promise<T> {
  const response = await fetch(`/api/connect/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok && !(data as { state?: string }).state)
    throw new Error(data.error || 'Something went wrong connecting. Please try again.');
  return data;
}

/** A QR code drawn as squares, so nothing from the server becomes markup. */
function QrCode({ link, provider }: { link: string; provider: CloudProvider }) {
  const cells = useMemo(() => {
    const code = qrcode(0, 'M');
    code.addData(link);
    code.make();
    const size = code.getModuleCount();
    const dark: Array<[number, number]> = [];
    for (let row = 0; row < size; row += 1)
      for (let column = 0; column < size; column += 1)
        if (code.isDark(row, column)) dark.push([column, row]);
    return { size, dark };
  }, [link]);
  return (
    <svg
      className="messenger-qr"
      viewBox={`-2 -2 ${cells.size + 4} ${cells.size + 4}`}
      role="img"
      aria-label={`QR code to scan with ${CLOUD_PROVIDER_NAMES[provider]}`}
      shapeRendering="crispEdges"
    >
      <rect x={-2} y={-2} width={cells.size + 4} height={cells.size + 4} fill="#fff" />
      {cells.dark.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="#0a0118" />
      ))}
    </svg>
  );
}

/**
 * Signing in with a messenger, or connecting it while signed in: agree, scan the code with the
 * phone, and type back the code the messenger then shows in the account's chat with itself.
 */
export function MessengerConnect({
  provider,
  token,
  onSignedIn,
  onSignInFirst,
  onConnected,
  onClose,
}: {
  provider: CloudProvider;
  /** Signed in, this connects the messenger to the account instead of signing in with it. */
  token?: string;
  onSignedIn?: (token: string) => void;
  /** The number has an account already: sign in to it by text, then call `resume`. */
  onSignInFirst?: (phoneNumber: string, resume: (token: string) => Promise<void>) => void;
  onConnected?: () => void;
  onClose: () => void;
}) {
  const name = CLOUD_PROVIDER_NAMES[provider];
  const how = HOW_TO_LINK[provider];
  const [step, setStep] = useState<'consent' | 'working'>('consent');
  const [login, setLogin] = useState<Login | null>(null);
  const [view, setView] = useState<ConnectLoginView>({ state: 'starting' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [answer, setAnswer] = useState('');
  const current = useRef<Login | null>(null);

  // A sign-in left behind, by Cancel or by closing the dialog, is ended on the service too, so
  // the messenger is let go at once rather than when the sign-in runs out.
  useEffect(
    () => () => {
      const open = current.current;
      current.current = null;
      if (open) void connectApi('cancel', open).catch(() => undefined);
    },
    []
  );

  const reset = () => {
    const open = current.current;
    current.current = null;
    if (open) void connectApi('cancel', open).catch(() => undefined);
    onClose();
  };

  const finish = (next: ConnectLoginView) => {
    setView(next);
    if (next.state === 'done') {
      current.current = null;
      if (next.token) onSignedIn?.(next.token);
      else onConnected?.();
    }
  };

  useEffect(() => {
    if (!login || ['done', 'failed', 'sign-in-first', 'confirm'].includes(view.state)) return;
    const timer = window.setTimeout(async () => {
      try {
        const next = await connectApi<ConnectLoginView>('status', login);
        if (current.current === login) setView(next);
      } catch {
        // The next poll tries again.
        if (current.current === login) setView((was) => ({ ...was }));
      }
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [login, view]);

  const start = async () => {
    setBusy(true);
    setError('');
    try {
      const started = await connectApi<Login & { error?: string }>(
        'start',
        {
          provider,
          termsVersion: TERMS_VERSION,
          // Agreeing on the screen that says it is agreeing to it.
          acceptsWhatsAppRisk: provider === 'whatsapp',
        },
        token
      );
      current.current = started;
      setLogin(started);
      setView({ state: 'starting' });
      setStep('working');
    } catch (startError) {
      setError(startError instanceof Error ? startError.message : 'Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!login) return;
    setBusy(true);
    setError('');
    try {
      if (view.state === 'password') {
        setView(
          await connectApi<ConnectLoginView>('password', { ...login, password: answer }, token)
        );
      } else {
        const next = await connectApi<ConnectLoginView>(
          'confirm',
          { ...login, code: answer.trim() },
          token
        );
        if (next.state === 'sign-in-first' && onSignInFirst) {
          // Handed over to finish after the text-code sign-in, so not cancelled on the way.
          current.current = null;
          onSignInFirst(next.phoneNumber, async (signedIn) => {
            finish(await connectApi<ConnectLoginView>('confirm', login, signedIn));
          });
        }
        finish(next);
      }
      setAnswer('');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (step === 'consent')
    return (
      <div className="messenger-connect">
        <p>
          SoulThread will keep {name} linked on its servers to read your messages and keep them in
          sync, the way the Mac app does, so you don&apos;t need a Mac. You can disconnect anytime
          in Settings, or remove it from {name} on your phone.
        </p>
        {how.note && <p>{how.note}</p>}
        <p className="sync-muted">
          By continuing you agree to the <Link to="/terms">Terms</Link> and{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
        <div className="messenger-connect-actions">
          <button className="btn btn-primary" type="button" disabled={busy} onClick={start}>
            {busy ? 'Starting…' : 'Agree and continue'}
          </button>
          <button className="sync-text-button" type="button" onClick={reset}>
            Cancel
          </button>
        </div>
        {error && (
          <p className="sync-error" role="alert">
            {error}
          </p>
        )}
      </div>
    );

  return (
    <div className="messenger-connect">
      {view.state === 'starting' && <p>Getting a code from {name}…</p>}
      {view.state === 'qr' && (
        <>
          <QrCode link={view.link} provider={provider} />
          <ol className="messenger-steps">
            {how.steps.map((instruction) => (
              <li key={instruction}>{instruction}</li>
            ))}
          </ol>
        </>
      )}
      {(view.state === 'password' || view.state === 'confirm') && (
        <form onSubmit={submit} className="sync-form">
          {view.state === 'password' ? (
            <>
              <label htmlFor="messenger-password">Your {name} password</label>
              <input
                id="messenger-password"
                type="password"
                autoFocus
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
                autoComplete="current-password"
                required
              />
              {view.hint && <p className="sync-muted">Hint: {view.hint}</p>}
              {view.wrong && (
                <p className="sync-error" role="alert">
                  That password wasn&apos;t right.
                </p>
              )}
            </>
          ) : (
            <>
              <label htmlFor="messenger-code">{how.code}</label>
              <input
                id="messenger-code"
                autoFocus
                pattern="[0-9]{6}"
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
              />
              {view.wrong && (
                <p className="sync-error" role="alert">
                  That code wasn&apos;t right.
                </p>
              )}
            </>
          )}
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      )}
      {view.state === 'sign-in-first' && (
        <p>
          {view.phoneNumber} already has a SoulThread account. Open Settings on a device already
          signed in to that account and connect {name} there. You can then sign in with {name}.
        </p>
      )}
      {view.state === 'done' && !view.token && (
        <p>{name} is connected. Your messages will appear over the next few minutes.</p>
      )}
      {view.state === 'failed' && (
        <>
          <p className="sync-error" role="alert">
            {view.reason}
          </p>
          <button className="btn btn-secondary" type="button" onClick={reset}>
            Start again
          </button>
        </>
      )}
      {!['done', 'failed'].includes(view.state) && (
        <button className="sync-text-button" type="button" onClick={reset}>
          Cancel
        </button>
      )}
      {error && (
        <p className="sync-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** The messengers people can sign in with right now, in the order to offer them; null until known. */
export function useMessengersAvailable(): CloudProvider[] | null {
  const [available, setAvailable] = useState<CloudProvider[] | null>(null);
  useEffect(() => {
    let current = true;
    fetch('/api/connect/available')
      .then((response) => (response.ok ? response.json() : {}))
      .then((answer: Partial<Record<CloudProvider, boolean>> & { region?: string }) => {
        if (current)
          setAvailable(
            orderMessengersForRegion(
              CLOUD_PROVIDERS.filter((provider) => answer[provider] === true),
              answer.region
            )
          );
      })
      .catch(() => {
        if (current) setAvailable([]);
      });
    return () => {
      current = false;
    };
  }, []);
  return available;
}

/** A button per messenger; choosing one opens its flow in place of the buttons. */
export function MessengerButtons({
  providers,
  verb,
  onChoose,
}: {
  providers: CloudProvider[];
  verb: 'Continue with' | 'Connect';
  onChoose: (provider: CloudProvider) => void;
}) {
  return (
    <div className="messenger-buttons">
      {providers.map((provider) => (
        <button
          key={provider}
          className={`btn btn-secondary messenger-connect-button messenger-${provider}`}
          type="button"
          onClick={() => onChoose(provider)}
        >
          {verb} {CLOUD_PROVIDER_NAMES[provider]}
        </button>
      ))}
    </div>
  );
}

/**
 * Messengers kept in sync on SoulThread's servers, shown in Settings with a way to stop each,
 * and whether the account's number was ever proven by a text code.
 */
export function useCloudConnections(token: string | null) {
  const [state, setState] = useState<{
    connections: CloudConnection[];
    numberVerifiedByText: boolean;
  }>({ connections: [], numberVerifiedByText: true });
  const load = useCallback(
    async (disconnecting?: CloudProvider) => {
      if (!token) return;
      const response = await fetch(`/api/connect${disconnecting ? `/${disconnecting}` : ''}`, {
        method: disconnecting ? 'DELETE' : 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) setState(await response.json());
    },
    [token]
  );
  useEffect(() => {
    void load();
  }, [load]);
  return {
    ...state,
    reload: () => load(),
    disconnect: (provider: CloudProvider) => load(provider),
  };
}

/**
 * For an account a messenger made: a text code to its number, entered while signed in, proves the
 * number too, so the Mac app and phone can sign in to it by text.
 */
export function VerifyNumberByText({
  token,
  phoneNumber,
  onVerified,
}: {
  token: string;
  phoneNumber: string;
  onVerified: (token: string) => void;
}) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const call = async (path: string, body: object) => {
    const response = await fetch(`/api/auth/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = (await response.json()) as { error?: string; token?: string };
    if (!response.ok) throw new Error(data.error || 'Please try again.');
    return data;
  };
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await work();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="messenger-connect">
      <p>Verify {phoneNumber} by text to use SoulThread on your Mac and phone too.</p>
      {sent ? (
        <form
          className="sync-form"
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              const { token: verified } = await call('verify-code', {
                phoneNumber,
                code: code.trim(),
              });
              if (verified) onVerified(verified);
            });
          }}
        >
          <label htmlFor="verify-number-code">Verification code</label>
          <input
            id="verify-number-code"
            autoFocus
            pattern="[0-9]{6}"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
          />
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Verify'}
          </button>
        </form>
      ) : (
        <button
          className="btn btn-secondary"
          type="button"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await call('send-code', { phoneNumber });
              setSent(true);
            })
          }
        >
          {busy ? 'Sending…' : 'Text me a code'}
        </button>
      )}
      {error && (
        <p className="sync-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
